import { useCallback, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, UserRound } from 'lucide-react';
import {
  Avatar,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@clarix/ui';
import { useAuthStore } from '@clarix/auth-store';
import { useSignOut } from './hooks/useSignOut';
import { UserMenuSlotTarget } from './UserMenuAppSlots';

interface UserMenuProps {
  trigger?: ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
}

export function UserMenu({ trigger, side = 'bottom', align = 'end' }: UserMenuProps) {
  const user = useAuthStore((s) => s.user);
  const { signOut, signingOut } = useSignOut();
  const navigate = useNavigate();

  // JwtPayload carries only `email` at the moment; the /me profile (with
  // display name) lands in the auth store later. Until then, derive a
  // display string and initials from the email local-part.
  const email = user?.email ?? '';
  const localPart = email.split('@')[0] ?? '';
  const name = localPart || 'User';
  const initials =
    localPart
      .split(/[._-]+/)
      .map((part) => part[0])
      .filter(Boolean)
      .join('')
      .slice(0, 2)
      .toUpperCase() || 'U';

  const handleSignOut = useCallback(() => {
    void signOut();
  }, [signOut]);

  return (
    // Non-modal on purpose: apps fill this menu from their OWN React tree
    // (UserMenuAppSlots), and a modal menu disables pointer events outside its
    // own box — which silently kills every control they portal in (a date
    // picker's calendar renders but cannot be clicked), and strands
    // `pointer-events: none` on <body> when an app dialog closes after it.
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        {trigger ?? (
          <button
            type="button"
            aria-label="User menu"
            className="hover:bg-accent flex items-center gap-1 rounded-md px-1 py-0.5 transition-colors"
          >
            <Avatar className="h-6 w-6">
              <AvatarFallback className="bg-blue-50 text-xs text-blue-800">
                {initials}
              </AvatarFallback>
            </Avatar>
          </button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side={side}
        align={align}
        className="w-48"
        sideOffset={8}
        // A popover opened by an app-contributed control mounts outside this
        // menu's DOM, and Radix cannot tell they belong together — the layer
        // stack is per-bundle, and remotes ship their own copy. Without this the
        // first click inside such a popover reads as "outside" and closes the
        // menu, taking the control with it.
        onInteractOutside={(e) => {
          const target = e.target as Element | null;
          if (target?.closest?.('[data-radix-popper-content-wrapper]')) e.preventDefault();
        }}
      >
        <DropdownMenuLabel className="px-2 py-1.5 font-normal">
          <p className="text-foreground truncate text-lg font-medium">{name}</p>
          {email && <p className="text-muted-foreground truncate text-sm">{email}</p>}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {/* App-contributed section (e.g. Support's availability picker), portaled
            in by the active app from its own provider tree. Renders its own
            trailing divider so nothing shows when no app fills the slot. */}
        <UserMenuSlotTarget />
        <DropdownMenuItem
          onClick={() => navigate('/profile')}
          className="cursor-pointer px-2 py-1.5 text-lg"
        >
          <UserRound className="mr-1.5 h-3.5 w-3.5" strokeWidth={1.5} />
          Profile
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={(e) => {
            e.preventDefault();
            handleSignOut();
          }}
          disabled={signingOut}
          className="text-destructive cursor-pointer px-2 py-1.5 text-lg"
        >
          <LogOut className="mr-1.5 h-3.5 w-3.5" strokeWidth={1.5} />
          {signingOut ? 'Signing out…' : 'Sign Out'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
